import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Car, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import T from '@/components/StyledTranslation';
import { AuthShell } from '@/components/AuthShell';
import axios from 'axios';
import SEO from '@/components/SEO';
import { API } from '@/config';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { login, user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [formError, setFormError] = useState('');

  // Placeholder translations for 9 languages
  const lang = i18n.language;
  const emailPlaceholders = {
    en: 'email@example.com', ar: 'email@example.com', sv: 'email@exempel.se',
    tr: 'email@ornek.com', fa: 'email@example.com', so: 'email@tusaale.com',
    pl: 'email@przyklad.pl', es: 'email@ejemplo.com', de: 'email@beispiel.de'
  };

  // Clear any lingering toasts when page loads
  useEffect(() => {
    toast.dismiss();
  }, []);

  // دالة لإنشاء جلسة الدفع مباشرة
  const createCheckoutSession = async (planId, authToken) => {
    try {
      const response = await fetch(`${API}/checkout/session?plan_id=${planId}&licenseType=${encodeURIComponent(localStorage.getItem('pendingPlanLicense') || 'b_korkort')}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.url) {
        // حذف البيانات المعلقة قبل التوجيه
        localStorage.removeItem('pendingPlanId');
        localStorage.removeItem('pendingPlanLicense');
        localStorage.removeItem('pendingPlanPrice');
        window.location.href = data.url;
        return true;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
    return false;
  };

  // Redirect if already logged in
  React.useEffect(() => {
    if (redirecting) return;
    if (isAuthenticated && user && token) {
      const pendingPlanId = localStorage.getItem('pendingPlanId');
      const pendingPlanPrice = localStorage.getItem('pendingPlanPrice');
      
      // إذا كانت هناك خطة مدفوعة معلقة - توجيه مباشر لنظام الدفع
      if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) > 0) {
        setRedirecting(true);
        createCheckoutSession(pendingPlanId, token);
        return;
      }
      
      // إذا كانت خطة مجانية - توجيه للاختبارات
      if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) <= 0) {
        localStorage.removeItem('pendingPlanId');
        localStorage.removeItem('pendingPlanLicense');
        localStorage.removeItem('pendingPlanPrice');
        navigate('/tests', { replace: true });
        return;
      }
      
      // توجيه المستخدم حسب نوع الاشتراك
      const isSubscribed = user?.subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';
      navigate(isSubscribed ? '/' : '/tests', { replace: true });
    }
  }, [isAuthenticated, user, navigate, redirecting, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[LOGIN] Form submitted', { email });
    setLoading(true);
    setFormError('');

    try {
      console.log('[LOGIN] Calling login function...');
      const userData = await login(email, password, i18n.language);
      console.log('[LOGIN] Login successful!', userData);
      toast.success(t('common.success'));
      
      // الحصول على التوكن من localStorage
      const authToken = localStorage.getItem('token');
      
      // التحقق من وجود خطة معلقة
      const pendingPlanId = localStorage.getItem('pendingPlanId');
      const pendingPlanPrice = localStorage.getItem('pendingPlanPrice');
      
      // إذا كانت خطة مدفوعة - توجيه مباشر لنظام الدفع
      if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) > 0) {
        setRedirecting(true);
        console.log('[LOGIN] Redirecting to Stripe checkout for paid plan');
        const success = await createCheckoutSession(pendingPlanId, authToken);
        if (!success) {
          // في حالة فشل إنشاء جلسة الدفع، توجيه لصفحة الأسعار
          navigate('/priser');
        }
        return;
      }
      
      // إذا كانت خطة مجانية - توجيه للاختبارات
      if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) <= 0) {
        localStorage.removeItem('pendingPlanId');
        localStorage.removeItem('pendingPlanLicense');
        localStorage.removeItem('pendingPlanPrice');
        console.log('[LOGIN] Redirecting to tests for free plan');
        navigate('/teoriprov');
        return;
      }
      
      // توجيه المستخدم حسب نوع الاشتراك
      const isSubscribed = userData?.subscription_status === 'active' || userData?.is_admin || userData?.role === 'super_admin';
      
      // التحقق من وجود صفحة محفوظة للتوجيه إليها
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        console.log('[LOGIN] Redirecting to saved path:', redirectPath);
        navigate(redirectPath);
        return;
      }
      
      console.log('[LOGIN] Navigating to', isSubscribed ? '/' : '/tests');
      navigate(isSubscribed ? '/' : '/tests');
    } catch (error) {
      console.error('[LOGIN] Login error:', error);
      const errorMessages = {
        sv: 'Ogiltiga inloggningsuppgifter. Kontrollera e-post och lösenord.',
        en: 'Invalid credentials. Please check your email and password.',
        ar: 'بيانات الدخول غير صحيحة. تحقق من البريد الإلكتروني وكلمة المرور.',
        tr: 'Geçersiz giriş bilgileri. E-posta ve şifrenizi kontrol edin.',
        fa: 'اطلاعات ورود نامعتبر است. ایمیل و رمز عبور را بررسی کنید.',
        so: 'Xogta gelitaanku waa khalad. Hubi emailka iyo furaha sirta.',
        pl: 'Nieprawidłowe dane logowania. Sprawdź e-mail i hasło.',
        es: 'Credenciales inválidas. Verifique su correo y contraseña.',
        de: 'Ungültige Anmeldedaten. Überprüfen Sie E-Mail und Passwort.'
      };
      const serverDetail = error.response?.data?.detail;
      const networkError = !error.response;
      let errorMsg;
      if (networkError) {
        const networkMessages = {
          sv: 'Kunde inte ansluta till servern. Försök igen om en stund.',
          en: 'Could not connect to server. Please try again shortly.',
          ar: 'تعذر الاتصال بالخادم. حاول مرة أخرى بعد قليل.',
          tr: 'Sunucuya bağlanılamadı. Lütfen biraz sonra tekrar deneyin.',
          fa: 'اتصال به سرور ممکن نیست. لطفاً کمی بعد دوباره امتحان کنید.',
          so: 'Lama xiri karin server-ka. Fadlan isku day mar kale.',
          pl: 'Nie można połączyć się z serwerem. Spróbuj ponownie za chwilę.',
          es: 'No se pudo conectar al servidor. Inténtelo de nuevo en un momento.',
          de: 'Verbindung zum Server nicht möglich. Bitte versuchen Sie es gleich noch einmal.'
        };
        errorMsg = networkMessages[lang] || networkMessages.en;
      } else {
        errorMsg = serverDetail || errorMessages[lang] || errorMessages.en;
      }
      setFormError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell mode="login" testId="login-page">
      <SEO page="login" />
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-1"><T k="auth.login" /></h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4"><T k="auth.noAccount" /> <Link to="/registrera" className="font-semibold text-emerald-600 hover:underline"><T k="auth.register" /></Link></p>

            {/* Social Login Buttons - Facebook temporarily hidden pending Meta Business Verification */}
            <div className="grid grid-cols-1 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const redirectUri = window.location.origin + '/auth/callback';
                    const response = await axios.get(`${API}/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}&source=login`);
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Error getting Google URL:', error);
                    toast.error(t('common.error'));
                  }
                }}
                className="h-12 rounded-xl border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-2"
                data-testid="google-login-btn"
                title={t('auth.googleSignIn')}
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{({ sv: 'Fortsätt med Google', ar: 'المتابعة عبر Google', en: 'Continue with Google', tr: 'Google ile devam et', fa: 'ادامه با Google', so: 'Ku sii wad Google', pl: 'Kontynuuj z Google', es: 'Continuar con Google', de: 'Weiter mit Google' })[i18n.language] || 'Continue with Google'}</span>
              </Button>

              {/* Facebook button hidden until Meta Business Verification is approved. Re-enable by setting SHOW_FACEBOOK to true. */}
              {false && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const ua = navigator.userAgent || navigator.vendor || '';
                    const isInApp = /FBAN|FBAV|FB_IAB|FB4A|Instagram|Messenger|TikTok|LinkedInApp|Line\/|MicroMessenger/i.test(ua);
                    if (isInApp) {
                      const msg = i18n.language === 'ar'
                        ? 'يرجى فتح هذا الرابط في متصفح Chrome أو Safari.\n\nانسخ: https://drivemaster.se/logga-in والصقه في Chrome'
                        : i18n.language === 'sv'
                          ? 'Öppna länken i Chrome eller Safari.\n\nKopiera: https://drivemaster.se/logga-in och klistra in i Chrome'
                          : 'Please open this link in Chrome or Safari.\n\nCopy: https://drivemaster.se/logga-in and paste in Chrome';
                      alert(msg);
                      return;
                    }
                    const redirectUri = window.location.origin + '/auth/callback';
                    const response = await axios.get(`${API}/auth/facebook/url?redirect_uri=${encodeURIComponent(redirectUri)}&source=login_fb`);
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Error getting Facebook URL:', error);
                    toast.error(t('common.error'));
                  }
                }}
                className="h-12 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white border-0 flex items-center justify-center gap-2"
                data-testid="facebook-login-btn"
                title={i18n.language === 'ar' ? 'متابعة عبر Facebook' : 'Continue with Facebook'}
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-semibold">Facebook</span>
              </Button>
              )}
            </div>
<div className="my-4">
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
                  {t('auth.or')}
                </span>
              </div>
            </div>

</div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Inline Error Message */}
            {formError && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="login-error-message">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{formError}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700"><T k="auth.email" /></Label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFormError(''); }}
                  className="ps-10 h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors"
                  placeholder={emailPlaceholders[lang] || emailPlaceholders.en}
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700"><T k="auth.password" /></Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFormError(''); }}
                  className="ps-10 pe-10 h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-emerald-600 hover:underline">
                  <T k="auth.forgotPassword" />
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold shadow-[0_10px_24px_-10px_rgba(16,185,129,0.9)] active:scale-[0.99] transition-transform"
              data-testid="login-submit"
            >
              {loading ? t('common.loading') : t('auth.loginBtn')}
            </Button>

          </form>
    </AuthShell>
  );
};

export default LoginPage;
